import {
  EActionCategory,
  EAppSupportType,
  ECardinality,
  EDatasourceType,
  EStatementTypes,
  IActionParameter,
  IAiActionInfo,
  IBlocklyActionInfo,
  IBluetoothCharacteristic,
  IJavaScriptActionInfo,
  IKType
} from "../../index";
import { EAiActionStatus } from "../enums/ai-action-status";
import { EActionSubCategory } from "../enums/datasource-type";
import { EManagedDbUpdateWhereConditionOperator, EManagedDbUpdateWhereType, ISymbol } from "../models/builder.dtos";

/**
 * User-facing documentation for a system action, read from the `Doc` block of
 * `Backend/CoreApi/Metadatas/Actions/*.json`. English only. Separate from `description`,
 * which is written for LLM tool docs. Absent on project/database actions.
 */
export interface IActionDocDto {
  /** Kuika Academy page (`https://academy.kuika.com/en/content/{slug}`). */
  url?: string;
  /** One short sentence shown in the action picker preview (≤ 160 chars). */
  summary?: string;
  /** Fuller explanation shown on hover of the info icon (≤ 600 chars). */
  details?: string;
  /** Parameter name → short description (≤ 100 chars). */
  parameters?: Record<string, string>;
}

export interface IActionDefinitionDto {
  id: string;
  name: string;
  createdAt?: string;
  createdUser?: {
    firstName?: string;
    lastName?: string;
    profilePhotoUrl?: string;
  };

  kDatasource_Id: string;
  kDatasource_Name: string;

  actionTemplate: string;
  statement: string;
  // typeName: string;
  returnTypeModel: IKType;
  returnTypeName: string;
  // className: string;
  /**
   * Source table this action reads/writes, when the backend resolved one.
   *
   * The column has always existed on `KAction` and Mapperly has always carried it
   * onto `ActionDefinitionModel`, so this value was already on the wire — only the
   * declaration was missing. Declared (optional) so the action catalog can expose a
   * TABLE BINDING: without it, matching a screen's data need against an existing
   * action degrades to comparing names, and Kuika has four live naming conventions
   * for the same five operations on one table (`ExpenseSearch` from the CRUD
   * generator, `SelectExpense` from its form path, `Expense_GetAll` legacy,
   * `ListExpenses` from the backend architect). Optional because it is empty for
   * actions with no single source table (REST, C#, SAP, aggregates over joins).
   */
  tableName?: string;
  code: string;
  isAsync: boolean;
  // errorMessage: string;
  actionType: EDatasourceType;
  returnCardinality: ECardinality;
  statementType: EStatementTypes;
  actionParameters: IActionParameter[];
  parameters: IActionParameter[];
  actionCategory: EActionCategory;
  actionSubCategory: EActionSubCategory;
  appSupportType: EAppSupportType;
  // bag: IBagDto;
  isDeprecated?: boolean;
  displayName?: string;
  hideDontTriggerValidationButton?: boolean;
  requiresConnection: boolean;
  bluetoothInfo: IBluetoothCharacteristic | null;
  aiActionInfo: IAiActionInfo | null;
  aiActionStatus?: EAiActionStatus;
  blocklyActionInfo: IBlocklyActionInfo | null;
  javaScriptActionInfo: IJavaScriptActionInfo | null;
  genericSelectData?: IGenericSelectDataModel;
  aiAgentInfo: string | null;
  mcpConfigSettingsGroupName?: string;
  sapActionInfo?: any;
  visionInfo?: string;
  isServerSideAction: boolean;
  isTagDisabled?: boolean;
  // KOA-16736: Persisted action description for MCP tool docs
  description?: string;
  /** User-facing academy documentation; see IActionDocDto. */
  doc?: IActionDocDto;
}

export interface IBagDto {
  targetType: IKType;
}

export interface IGenericSelectDataModel {
  tables: IGenericSelectTablesModel[];
  fields: IGenericSelectDataField[];
  calculatedFields?: IGenericSelectCalculatedField[];
  relations: IGenericSelectRelationsModel[];
  whereData: IGenericSelectWhereDataModel[];
  sortData: IGenericSelectSortDataModel[];
  statementData?: IGenericSelectStatementData;
  hasValidationError?: boolean;
  enablePagination?: boolean;
}

export interface IGenericSelectTablesModel {
  id?: string;
  name: string;
  schema: string;
  alias?: string; // Used when same table is joined multiple times via different FKs, e.g., "Customers[CustomerId]"
  joinedViaFk?: string; // The FK column name this table was joined via
  validations?: {
    name?: boolean;
  };
}

export interface IGenericSelectDataField {
  tableName: string;
  tableSchema: string;
  tableId?: string | null;
  tableAlias?: string; // Used when same table is joined multiple times, e.g., "Customers[CustomerId]"
  colName: string;
  colSqlDataType: string;
  groupBy?: EGenericSelectDataGroupBy;
  sortType?: ESortType;
  alias: string;
  // Symbol for calculated fields - allows using symbol picker instead of table+column selection
  paramSymbol?: ISymbol;
  validations?: {
    tableName?: boolean;
    colName?: boolean;
    groupBy?: boolean;
    sortType?: boolean;
    alias?: boolean;
    paramSymbol?: boolean;
  };
}

export interface IGenericSelectRelationsModel {
  fromTable: string;
  fromTableSchema: string;
  fromTableId?: string;
  fromFk: string;
  toTable: string;
  toTableSchema: string;
  toTableId?: string;
  toPk: string;
  joinType: EJoinType;
  validations?: {
    fromTable?: boolean;
    fromTableSchema?: boolean;
    fromFk?: boolean;
    toTable?: boolean;
    toTableSchema?: boolean;
    toPk?: boolean;
    joinType?: boolean;
  };
}

export interface IGenericSelectWhereDataModel {
  type: EManagedDbUpdateWhereType;
  andCondition?: IGenericSelectWhereConditionModel;
  orConditions?: IGenericSelectWhereConditionModel[];
}

export interface IGenericSelectWhereConditionModel {
  tableName: string;
  tableSchema: string;
  tableId?: string;
  tableAlias?: string; // Used when same table is joined multiple times
  colName: string;
  colSqlDataType: string;
  conditionOperator: EManagedDbUpdateWhereConditionOperator;
  paramSymbol: ISymbol;
  validations?: {
    tableName?: boolean;
    colName?: boolean;
    conditionOperator?: boolean;
    paramSymbol?: boolean;
  };
}

export interface IGenericSelectSortDataModel {
  tableName: string;
  tableSchema: string;
  tableId?: string;
  tableAlias?: string; // Used when same table is joined multiple times
  colName: string;
  sortType: ESortType;
  validations?: {
    tableName?: boolean;
    colName?: boolean;
    sortType?: boolean;
  };
}

export interface IGenericSelectStatementData {
  statementLanguage: EStatementLanguage;
  statement: string;
  createdAt: Date;
  isConvertedToSql: boolean;
}

export enum EJoinType {
  leftJoin = 1,
  rightJoin = 2,
  innerJoin = 3,
  fullOuterJoin = 4
}

export enum ESortType {
  ascending = 1,
  descending = 2
}

export enum EStatementLanguage {
  MsSql = 1
}

export enum EGenericSelectDataGroupBy {
  Count = 1,
  Min = 2,
  Max = 3
}

export enum ECalculatedFieldFunction {
  // Date functions
  DateDiff = 1,
  // Window functions
  RowNumber = 2
}

export enum EDateDiffPart {
  Day = 1,
  Month = 2,
  Year = 3,
  Hour = 4,
  Minute = 5,
  Second = 6,
  Week = 7
}

export interface IGenericSelectCalculatedField {
  alias: string;
  function: ECalculatedFieldFunction;
  // Fields used by the function (e.g., CONCAT(field1, field2), SUM(field1))
  fields: IGenericSelectDataField[];
  // For DATEDIFF - date part (day, month, year, hour, minute, second, week)
  dateDiffPart?: EDateDiffPart;
  // For ROWNUMBER - PARTITION BY (optional)
  partitionByFields?: IGenericSelectDataField[];
  // For ROWNUMBER - ORDER BY (required) - uses sortType from IGenericSelectDataField
  orderByFields?: IGenericSelectDataField[];
  // GroupBy for calculated fields (similar to regular fields)
  groupBy?: EGenericSelectDataGroupBy;
  // UI state: whether the calculated field is collapsed (saved)
  isCollapsed?: boolean;
  validations?: {
    alias?: boolean;
    function?: boolean;
    fields?: boolean;
    orderByFields?: boolean;
    groupBy?: boolean;
  };
}

export interface IAiTemplateRequest {
  datasourceId: string;
  actionId?: string;
  aiTemplateId: string;
  actionName: string;
}

export interface IAiModel {
  id: string;
  name: string;
  description: string;
  categories?: number[];
  providerId?: string;
  status?: number;
  helpUrl?: string;
  features?: IAiModelFeatures;
}

export interface IAiModelFeatures {
  temperature?: IAiModelFeature;
  maxLength?: IAiModelFeature;
  topP?: IAiModelFeature;
  frequencyPenalty?: IAiModelFeature;
  presencePenalty?: IAiModelFeature;
  stopSequences?: IAiModelFeature;
}

export interface IAiModelFeature {
  supported: boolean;
  min?: number;
  max?: number;
}
export interface IAiTemplate {
  id: string;
  modelId: string;
  name: string;
  description: string;
  shortDescription?: string;
  modelParameters: any;
  parameters: any;
  returnTypeName: string;
  returnType: any;
  imageUrl: string;
  tags?: string[];
  aiReturnTypeTemplate?: string | any;
  editable?: boolean;
  exampleReturnDto?: string | any;
}

// KOA-16736: DTOs for AI-powered action description generation/save
export interface IGenerateActionDescriptionsRequest {
  actionIds: string[];
  modelName?: string;
  includeParameterDescriptions?: boolean;
}

export interface IGenerateActionDescriptionsResponse {
  results: IActionDescriptionResult[];
  totalProcessed: number;
  successCount: number;
  failureCount: number;
}

export interface IActionDescriptionResult {
  actionId: string;
  actionName: string;
  success: boolean;
  errorMessage?: string;
  description?: string;
  analyzedSource?: string;
}

export interface ISaveActionDescriptionsRequest {
  actionId: string;
  description: string;
}

export interface ISaveActionDescriptionsResponse {
  success: boolean;
  message: string;
  actionId: string;
}
